"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@workspace/ui/components";
import { useDialogs } from "@/lib/hooks/use-dialogs";
import { AnimatePresence, motion } from "motion/react";
import Image from "next/image";
import Sparkles from "@/public/icons/sparkles.svg";

export function MarcusChallengeDialog() {
  const router = useRouter();
  const { isOpen, closeDialog } = useDialogs();
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(1);

  const open = isOpen("marcus-challenge");

  const questions = [
    {
      id: 1,
      title: "Can you save your SaaS?",
      description:
        'Kai is responsible for the tools the sales team uses. Kai has decided that Saasforce is "too heavy" for his "Modern Sales Squad." He is currently in the process of churning your contract because he believes he can replace the entire CRM stack with a few "AI wrappers" he found on Product Hunt.',
    },
    {
      id: 2,
      title: "Your Angle",
      description:
        'Marcus, after eight years of being Kai\'s "tech therapist," you\'re about to face a version of him that has traded your partnership for a Patagonia vest and a Claude-generated prayer. To him, your Saassforce contract is a "legacy anchor," and he\'s one delusional "force-push" away from replacing eight years of SOC2-compliant data with a "RizzForce" AI wrapper that treats customer relationships as simple "vibes."',
    },
    {
      id: 3,
      title: "What You'll Discover",
      description: `Kai is non-technical but uses technical words like "API," "LLM," and "Vector DB" as if they are magic spells. He values "Simplicity" and "Flashiness" over "Architecture" and "Reliability." He’s been in the role for 18 months and is bored with "Standard Objects." He isn't looking for a better CRM; he’s looking for a tool that makes him look like a "Tech Visionary" to his bosses.`,
    },
    {
      id: 4,
      title: "Why this matters?",
      description: `Apex Dynamics has been a "Blue Chip" Saasforce account for a decade. Kai is using this "pivot" to build his internal brand as an innovator. He has told the VP of Sales that he can "cut the tech spend by 80%" by switching to a "Vibe-First" AI workflow. If he succeeds, he gets a promotion; if he fails, the sales team loses a decade of customer data. He’s willing to take that risk for the "Innovation Award" at the next company retreat.`,
    },
  ];

  const toggleQuestion = (id: number) => {
    setExpandedQuestion((prev) => (prev === id ? null : id));
  };

  const handleStartChallenge = () => {
    closeDialog("marcus-challenge");
    router.push("/training/overview");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          closeDialog("marcus-challenge");
        }
      }}
    >
      <DialogContent
        className="max-w-[calc(100%-2rem)] md:max-w-2xl max-h-[80vh] overflow-hidden p-5 md:p-5 flex flex-col gap-6 md:gap-8 rounded-[40px]"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Try the Marcus Challenge!</DialogTitle>
        {/* Image container */}
        <div className="relative shrink-0 w-full aspect-video overflow-hidden rounded-[28px]">
          <video
            src="/landing/video/just_business.mp4"
            className="absolute top-1/2 left-1/2 -translate-1/2 w-full h-[105%] object-cover"
            autoPlay
            playsInline
          />
        </div>

        <div className="flex items-start gap-2.5 w-full px-2">
          <div className="flex size-6 shrink-0 items-center justify-center">
            <Sparkles className="[&>path]:fill-[#8141C2]" />
          </div>
          <div className="flex flex-col gap-1.5">
            <h2 className="text-lg md:text-[22px] font-semibold leading-6">
              Try the Marcus Challenge!
            </h2>
            <p className="font-medium leading-[22px] text-d-gray">
              Your client is about to walk away with $350K for a vibe coded
              solution
            </p>
          </div>
        </div>

        {/* Questions container with scroll */}
        <div className="flex-1 overflow-y-auto min-h-0 flex flex-col gap-4 px-1 hidden">
          {questions.map((question) => (
            <div key={question.id} className="flex flex-col gap-2">
              <button
                onClick={() => toggleQuestion(question.id)}
                className="flex items-center gap-2.5 w-full py-2 px-2.5 rounded-lg transition-colors hover:bg-[#faf6f1] active:bg-d-secondary"
              >
                <div className="shrink-0 w-6 h-6 rounded-full bg-d-secondary flex items-center justify-center">
                  <span className="text-sm font-extrabold text-d-black tracking-tight">
                    {question.id}
                  </span>
                </div>

                <span className="flex-1 text-left text-base font-medium text-d-black leading-6">
                  {question.title}
                </span>

                <ChevronDown
                  className={`w-5 h-5 text-d-gray shrink-0 transition-transform duration-300 ease-in-out ${
                    expandedQuestion === question.id ? "rotate-180" : ""
                  }`}
                />
              </button>

              <AnimatePresence initial={false}>
                {expandedQuestion === question.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      height: { duration: 0.3, ease: "easeInOut" },
                      opacity: { duration: 0.25, ease: "easeInOut" },
                    }}
                    className="overflow-hidden"
                  >
                    <div className="pl-11 pr-2 pt-1 pb-2 text-sm font-medium text-d-gray leading-5">
                      {question.description}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        {/* CTA section */}
        <div className="shrink-0 flex flex-col gap-3 items-center">
          <button
            onClick={handleStartChallenge}
            className="w-full h-12 px-5 py-[13px] flex items-center justify-center gap-2.5 rounded-xl text-white text-lg font-semibold capitalize whitespace-nowrap transition-all duration-300 relative overflow-hidden group border-2 border-[#ad5dfe] shadow-[0px_4px_4px_0px_rgba(41,37,36,0.12)] cursor-pointer"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255, 255, 255, 0.56) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(90deg, rgb(129, 65, 194) 0%, rgb(129, 65, 194) 100%)",
            }}
          >
            <span
              className="absolute top-0 left-0 w-full h-full pointer-events-none bg-bottom-left group-hover:bg-top-right transition-all duration-700"
              style={{
                backgroundImage:
                  "linear-gradient(-45deg,rgba(0, 0, 0, 0) 40%, rgba(255, 255, 255, .7) 50%, rgba(0, 0, 0, 0) 60%)",
                backgroundSize: "500% 500%",
              }}
            />
            <Image
              src="/icons/sparkles.svg"
              alt="sparkles"
              width={20}
              height={20}
            />
            Start Free Challenge Now
          </button>

          <p className="text-center text-sm font-medium text-[#b1a99e] leading-4">
            No login required • 3 minutes • See what makes Yolk different
          </p>

          <button
            onClick={() => closeDialog("marcus-challenge")}
            className="text-center text-sm font-semibold text-d-gray underline hover:text-d-black transition-colors capitalize cursor-pointer"
          >
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
